import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gavel, Clock, TrendingUp, Eye, EyeOff, Bot,
  ArrowLeft, Zap, Trophy, Users, AlertCircle, CheckCircle2,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { io } from "socket.io-client";

dayjs.extend(relativeTime);
dayjs.locale("es");

const BASE_URL = (process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") || "http://localhost:4000").replace(/\/$/, "");

// ── Countdown inline ─────────────────────────────────────────────────────────
function Countdown({ endAt, onExpire }) {
  const [parts, setParts] = useState({ h: 0, m: 0, s: 0, expired: false });

  useEffect(() => {
    const calc = () => {
      const diff = dayjs(endAt).diff(dayjs(), "second");
      if (diff <= 0) { setParts({ expired: true }); onExpire?.(); return; }
      setParts({ h: Math.floor(diff / 3600), m: Math.floor((diff % 3600) / 60), s: diff % 60, expired: false, urgent: diff < 300 });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [endAt, onExpire]);

  if (parts.expired) return <span className="text-red-500 font-bold">Finalizada</span>;

  const Seg = ({ n, label }) => (
    <div className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg min-w-[44px] ${parts.urgent ? "bg-red-50 border border-red-200" : "bg-gray-50 border border-gray-200"}`}>
      <span className={`text-lg font-extrabold font-mono leading-none ${parts.urgent ? "text-red-600 animate-pulse" : "text-gray-800"}`}>
        {String(n).padStart(2, "0")}
      </span>
      <span className="text-[9px] text-gray-400 uppercase font-semibold mt-0.5">{label}</span>
    </div>
  );

  return (
    <div className="flex items-center gap-1.5">
      {parts.h > 0 && <Seg n={parts.h} label="h" />}
      <Seg n={parts.m} label="min" />
      <Seg n={parts.s} label="seg" />
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auction,      setAuction]      = useState(null);
  const [bids,         setBids]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [bidAmount,    setBidAmount]    = useState("");
  const [autoBidMax,   setAutoBidMax]   = useState("");
  const [showAuto,     setShowAuto]     = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [watching,     setWatching]     = useState(false);
  const [imgIdx,       setImgIdx]       = useState(0);
  const [myAutoBidMax, setMyAutoBidMax] = useState(null);
  const [updatingAuto, setUpdatingAuto] = useState(false);
  const socketRef = useRef(null);

  const user  = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("accessToken") || localStorage.getItem("token");

  const loadAuction = useCallback(async () => {
    try {
      const [aRes, bRes] = await Promise.all([
        API.get(`/auctions/${id}`),
        API.get(`/auctions/${id}/bids`),
      ]);
      const a = aRes.data?.data?.auction;
      setAuction(a);
      const loadedBids = bRes.data?.data?.bids ?? [];
      setBids(loadedBids);
      const minBid = Number(a.currentPrice) + Number(a.minIncrement);
      setBidAmount(minBid.toFixed(2));
      if (user) {
        const myBid = loadedBids.find((b) => b.user?.id === user.id || b.userId === user.id);
        if (myBid?.autoBidMax) {
          setMyAutoBidMax(Number(myBid.autoBidMax));
          setAutoBidMax(Number(myBid.autoBidMax).toFixed(2));
        }
      }
    } catch {
      toast.error("Error cargando la subasta");
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { loadAuction(); }, [loadAuction]);

  useEffect(() => {
    if (!token) return;
    const socket = io(BASE_URL, { auth: { token } });
    socketRef.current = socket;
    socket.emit("auction:join", id);

    socket.on("auction:bid", (data) => {
      setAuction((prev) => prev ? { ...prev, currentPrice: data.currentPrice, totalBids: data.totalBids, endAt: data.endAt } : prev);
      setBids((prev) => [{
        ...data.bid,
        user: data.bid.userId === user?.id
          ? { id: user.id, name: "Tú", avatar: user.avatar }
          : { id: data.bid.userId, name: "Pujador", avatar: null },
      }, ...prev].slice(0, 50));
      setBidAmount((Number(data.currentPrice) + Number(auction?.minIncrement ?? 1)).toFixed(2));
    });
    socket.on("auction:outbid",   (data) => toast.warn(`⚡ ${data.message} — $${Number(data.currentPrice).toLocaleString("es-CO")}`, { autoClose: 5000 }));
    socket.on("auction:sold",     (data) => { toast.success("🏆 ¡Finalizada con Cómpralo Ya!"); setAuction((p) => p ? { ...p, status: "sold", currentPrice: data.currentPrice, winnerId: data.winnerId } : p); });
    socket.on("auction:ended",    (data) => { setAuction((p) => p ? { ...p, status: "ended", winnerId: data.winnerId } : p); if (data.winnerId === user?.id) toast.success("🏆 ¡Ganaste!"); });
    socket.on("auction:cancelled", ()   => { toast.info("Subasta cancelada"); setAuction((p) => p ? { ...p, status: "cancelled" } : p); });

    return () => { socket.emit("auction:leave", id); socket.disconnect(); };
  // eslint-disable-next-line
  }, [id, token]);

  const handleBid = async (buyNow = false) => {
    if (!user) { toast.warn("Inicia sesión para pujar"); return; }
    setSubmitting(true);
    try {
      const amount = buyNow ? Number(auction.buyNowPrice) : Number(bidAmount);
      const body = { amount };
      if (!buyNow && showAuto && autoBidMax) body.autoBidMax = Number(autoBidMax);
      await API.post(`/auctions/${id}/bids`, body);
      toast.success(buyNow ? "¡Cómpralo ya completado!" : "¡Puja registrada!");
      setBidAmount((amount + Number(auction.minIncrement)).toFixed(2));
      if (!buyNow && showAuto && autoBidMax) setMyAutoBidMax(Number(autoBidMax));
      else if (buyNow) { setAutoBidMax(""); setMyAutoBidMax(null); }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error al pujar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAutoBid = async () => {
    if (!autoBidMax) { toast.warn("Ingresa un límite"); return; }
    setUpdatingAuto(true);
    try {
      await API.patch(`/auctions/${id}/auto-bid`, { autoBidMax: Number(autoBidMax) });
      setMyAutoBidMax(Number(autoBidMax));
      toast.success("Límite actualizado");
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error");
    } finally {
      setUpdatingAuto(false);
    }
  };

  const handleCancelAutoBid = async () => {
    setUpdatingAuto(true);
    try {
      await API.patch(`/auctions/${id}/auto-bid`, { autoBidMax: null });
      setMyAutoBidMax(null); setAutoBidMax(""); setShowAuto(false);
      toast.info("Puja automática desactivada");
    } catch { } finally { setUpdatingAuto(false); }
  };

  const toggleWatch = async () => {
    if (!user) { toast.warn("Inicia sesión"); return; }
    try {
      if (watching) { await API.delete(`/auctions/${id}/watch`); setWatching(false); }
      else          { await API.post(`/auctions/${id}/watch`);   setWatching(true);  }
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin w-9 h-9 border-4 border-supernova-400 border-t-transparent rounded-full" />
    </div>
  );

  if (!auction) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
      Subasta no encontrada
    </div>
  );

  const isActive = auction.status === "active";
  const isEnded  = ["ended", "sold", "cancelled"].includes(auction.status);
  const iWon     = isEnded && auction.winnerId === user?.id;
  const minBid   = Number(auction.currentPrice) + Number(auction.minIncrement);
  const images   = auction.images ?? [];

  const statusConfig = {
    active:    { label: "🔴 En vivo",    cls: "bg-green-500 text-white" },
    scheduled: { label: "🔵 Próxima",    cls: "bg-blue-500 text-white"  },
    ended:     { label: "Finalizada",    cls: "bg-gray-500 text-white"  },
    sold:      { label: "Vendida",       cls: "bg-supernova-400 text-black" },
    cancelled: { label: "Cancelada",     cls: "bg-red-500 text-white"   },
  };
  const sc = statusConfig[auction.status] ?? statusConfig.ended;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Topbar */}
      <div className="bg-white border-b border-gray-100 px-3 py-2.5 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition">
          <ArrowLeft size={16} /> Subastas
        </button>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${sc.cls}`}>{sc.label}</span>
          <button
            onClick={toggleWatch}
            className={`p-1.5 rounded-lg transition ${watching ? "bg-supernova-100 text-supernova-600" : "text-gray-400 hover:bg-gray-100"}`}
            title={watching ? "Dejar de seguir" : "Seguir subasta"}
          >
            {watching ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 grid lg:grid-cols-[1fr_360px] gap-4">

        {/* ── Columna izquierda ──────────────────────────────── */}
        <div className="space-y-3">

          {/* Imágenes */}
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            <div className="relative aspect-video bg-gray-100">
              {images.length > 0 ? (
                <img
                  src={images[imgIdx]?.startsWith("http") ? images[imgIdx] : `${BASE_URL}/${images[imgIdx]}`}
                  alt={auction.title}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Gavel size={48} className="text-gray-200" />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setImgIdx(i)}
                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 flex-shrink-0 transition ${imgIdx === i ? "border-supernova-400" : "border-transparent opacity-60 hover:opacity-100"}`}
                  >
                    <img src={img?.startsWith("http") ? img : `${BASE_URL}/${img}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info del artículo */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div>
              {auction.company && (
                <Link to={`/company/${auction.company.id}`} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 mb-1.5 transition">
                  {auction.company.logo && <img src={`${BASE_URL}/${auction.company.logo}`} alt="" className="w-4 h-4 rounded-full object-cover" />}
                  {auction.company.name}
                </Link>
              )}
              <h1 className="text-lg font-bold text-gray-900 leading-snug">{auction.title}</h1>
              {auction.description && (
                <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{auction.description}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-50">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Precio inicio</p>
                <p className="text-sm font-bold text-gray-700 mt-0.5">${Number(auction.startingPrice).toLocaleString("es-CO")}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Incremento</p>
                <p className="text-sm font-bold text-gray-700 mt-0.5">${Number(auction.minIncrement).toLocaleString("es-CO")}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Pujas</p>
                <p className="text-sm font-bold text-gray-700 mt-0.5 flex items-center gap-1"><TrendingUp size={12} /> {auction.totalBids}</p>
              </div>
              {auction.buyNowPrice && (
                <div className="col-span-3 bg-supernova-50 rounded-lg p-2.5 border border-supernova-200 flex items-center justify-between">
                  <span className="text-xs text-supernova-700 font-semibold">Cómpralo ya</span>
                  <span className="text-sm font-extrabold text-supernova-700">${Number(auction.buyNowPrice).toLocaleString("es-CO")}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Columna derecha ────────────────────────────────── */}
        <div className="space-y-3">

          {/* Precio actual + countdown */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Precio actual</p>
            <AnimatePresence mode="wait">
              <motion.p
                key={Number(auction.currentPrice)}
                initial={{ scale: 1.1, color: "#ca8a04" }}
                animate={{ scale: 1,   color: "#111827" }}
                transition={{ duration: 0.4 }}
                className="text-3xl font-extrabold text-gray-900 mb-3"
              >
                ${Number(auction.currentPrice).toLocaleString("es-CO")}
              </motion.p>
            </AnimatePresence>

            {isActive && (
              <div>
                <p className="text-xs text-gray-400 flex items-center gap-1 mb-2">
                  <Clock size={10} /> Tiempo restante
                </p>
                <Countdown endAt={auction.endAt} onExpire={() => setAuction((p) => p ? { ...p, status: "ended" } : p)} />
              </div>
            )}

            {auction.status === "scheduled" && (
              <div className="flex items-center gap-2 text-sm">
                <Clock size={13} className="text-blue-500" />
                <span className="text-blue-600 font-semibold">{dayjs(auction.startAt).format("DD MMM YYYY · HH:mm")}</span>
              </div>
            )}
          </div>

          {/* Ganador */}
          {isEnded && auction.winner && (
            <div className={`rounded-xl p-3.5 flex items-center gap-3 ${iWon ? "bg-supernova-400" : "bg-zinc-800"}`}>
              <Trophy size={22} className={iWon ? "text-black" : "text-yellow-400"} />
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${iWon ? "text-black/60" : "text-gray-400"}`}>Ganador</p>
                <p className={`font-bold text-sm truncate ${iWon ? "text-black" : "text-white"}`}>
                  {iWon ? "🎉 ¡Tú ganaste!" : auction.winner.name}
                </p>
                <p className={`text-xs font-bold ${iWon ? "text-black/70" : "text-yellow-400"}`}>
                  ${Number(auction.currentPrice).toLocaleString("es-CO")}
                </p>
              </div>
            </div>
          )}

          {/* Formulario de puja */}
          {isActive && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <Gavel size={15} className="text-supernova-500" /> Hacer una puja
              </h3>

              {/* Input de monto */}
              <div>
                <label className="text-xs text-gray-400 font-medium block mb-1">
                  Monto (mín. ${minBid.toLocaleString("es-CO")})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={minBid}
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-base font-bold focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  placeholder={`Mín. $${minBid.toLocaleString("es-CO")}`}
                />
              </div>

              {/* Auto-bid */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAuto(!showAuto)}
                  className={`flex items-center gap-1.5 text-xs font-semibold transition ${showAuto || myAutoBidMax ? "text-supernova-600" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <Bot size={13} />
                  Puja automática
                  {myAutoBidMax ? (
                    <span className="px-2 py-0.5 rounded-full bg-supernova-400 text-black text-[10px] font-bold">
                      Activa · ${Number(myAutoBidMax).toLocaleString("es-CO")}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px]">Off</span>
                  )}
                </button>

                {(showAuto || myAutoBidMax) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-2 p-3 bg-supernova-50 rounded-lg border border-supernova-200 space-y-2"
                  >
                    <p className="text-xs text-supernova-700 flex items-center gap-1">
                      <AlertCircle size={11} />
                      El sistema pujará automáticamente hasta este límite
                    </p>
                    <input
                      type="number"
                      step="0.01"
                      min={bidAmount}
                      value={autoBidMax}
                      onChange={(e) => setAutoBidMax(e.target.value)}
                      className="w-full border border-supernova-300 rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white"
                      placeholder="Límite máximo"
                    />
                    {myAutoBidMax && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleUpdateAutoBid}
                          disabled={updatingAuto}
                          className="flex-1 bg-supernova-400 hover:bg-supernova-500 disabled:opacity-60 text-black font-bold py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition"
                        >
                          {updatingAuto
                            ? <span className="animate-spin w-3 h-3 border-2 border-black border-t-transparent rounded-full" />
                            : <><CheckCircle2 size={11} /> Actualizar</>
                          }
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelAutoBid}
                          disabled={updatingAuto}
                          className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 font-semibold rounded-lg text-xs transition disabled:opacity-60"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              <button
                onClick={() => handleBid(false)}
                disabled={submitting || !user}
                className="w-full bg-supernova-400 hover:bg-supernova-500 disabled:opacity-60 text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition"
              >
                {submitting
                  ? <span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                  : <><Gavel size={15} /> Pujar ${Number(bidAmount || minBid).toLocaleString("es-CO")}</>
                }
              </button>

              {auction.buyNowPrice && (
                <button
                  onClick={() => handleBid(true)}
                  disabled={submitting || !user}
                  className="w-full bg-zinc-800 hover:bg-zinc-900 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 transition text-sm"
                >
                  <Zap size={14} className="text-yellow-400" />
                  Cómpralo ya · ${Number(auction.buyNowPrice).toLocaleString("es-CO")}
                </button>
              )}

              {!user && (
                <Link to="/login" className="block text-center text-xs text-supernova-600 font-semibold hover:underline">
                  Inicia sesión para pujar
                </Link>
              )}
            </div>
          )}

          {/* Historial de pujas */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <Users size={14} className="text-gray-400" /> Pujas
              </h3>
              <span className="text-xs text-gray-400">{bids.length}</span>
            </div>

            {bids.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-5">Sé el primero en pujar</p>
            ) : (
              <div className="space-y-1 max-h-56 overflow-y-auto pr-0.5">
                {bids.map((bid, i) => (
                  <motion.div
                    key={bid.id}
                    initial={i === 0 ? { backgroundColor: "#FEF08A" } : {}}
                    animate={{ backgroundColor: i === 0 ? "#f9fafb" : "transparent" }}
                    transition={{ duration: 1.2 }}
                    className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg"
                  >
                    <div className="w-7 h-7 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                      {bid.user?.avatar
                        ? <img src={bid.user.avatar} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-500">
                            {bid.user?.name?.[0]?.toUpperCase() ?? "?"}
                          </div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 truncate flex items-center gap-1">
                        {bid.user?.name ?? "Usuario"}
                        {bid.autoBidMax && <Bot size={9} className="text-supernova-500" />}
                        {i === 0 && bid.isWinning && <CheckCircle2 size={9} className="text-green-500" />}
                      </p>
                      <p className="text-[10px] text-gray-400">{dayjs(bid.createdAt).fromNow()}</p>
                    </div>
                    <p className={`font-bold text-sm flex-shrink-0 ${i === 0 ? "text-supernova-600" : "text-gray-500"}`}>
                      ${Number(bid.amount).toLocaleString("es-CO")}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
